import React, { useState, useCallback, useRef } from 'react';
import { Box, Chip, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction,
  GroupQueryOptions,
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import ShipmentDrawer from './shipment-drawer';
import UpdateShipmentDrawer from './UpdateShipmentDrawer';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { Shipment } from 'src/types/Shipment.types';

enum ShipmentStatus {
  Processing = 0,
  Expected = 1,
  InReception = 2,
  Received = 3,
  Allocated = 4,
  Cancelled = 5,
  Analyzing = 6,
  Analyzed = 7,
  Completing = 8,
  Completed = 9
}

const ShipmentListing: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [updateDrawerOpen, setUpdateDrawerOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleFileDownload = useCallback(async (fileUrl: string, fileName?: string, shipment?: any) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const downloadFileName = fileName || fileUrl.split('/').pop() || 'shipment-file';
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(
        showAlert({
          message: `File downloaded successfully: ${downloadFileName}`,
          type: 'success'
        })
      );
    } catch (error) {
      console.error('Error downloading file:', error);
      dispatch(
        showAlert({
          message: 'Failed to download file. Please try again.',
          type: 'error'
        })
      );
      window.open(fileUrl, '_blank');
    }
  }, [dispatch]);

  const renderStatus = useCallback((params: GridRenderCellParams) => {
    const status = params.value as ShipmentStatus;
    let color = '#757575';
    let label = 'Unknown';

    switch (status) {
      case ShipmentStatus.Processing:
        color = '#9c27b0'; label = 'Processing'; break;
      case ShipmentStatus.Expected:
        color = '#ff9800'; label = 'Expected'; break;
      case ShipmentStatus.InReception:
        color = '#2196f3'; label = 'In Reception'; break;
      case ShipmentStatus.Received:
        color = '#4caf50'; label = 'Received'; break;
      case ShipmentStatus.Allocated:
        color = '#388e46ff'; label = 'Allocated'; break;
      case ShipmentStatus.Cancelled:
        color = '#f44336'; label = 'Cancelled'; break;
      case ShipmentStatus.Analyzing:
        color = '#3f51b5'; label = 'Analyzing'; break;
      case ShipmentStatus.Analyzed:
        color = '#1976d2'; label = 'Analyzed'; break;
      case ShipmentStatus.Completing:
        color = '#7b1fa2'; label = 'Completing'; break;
      case ShipmentStatus.Completed:
        color = '#2e7d32'; label = 'Completed'; break;
    }
    
    return (
      <Chip
        label={label}
        sx={{
          background: color,
          color: '#fff',
          fontWeight: 600,
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}
        size="small"
      />
    );
  }, []);

  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);
  const handleUpdateDrawerClose = useCallback(() => setUpdateDrawerOpen(false), []);
  const handleAddShipment = useCallback(() => setDrawerOpen(true), []);

  const handleEditShipment = useCallback((shipment: Shipment) => {
    setSelectedShipment(shipment);
    setUpdateDrawerOpen(true);
  }, []);

  const mapFieldForGrouping = useCallback((field: string): string => {
    const fieldMapping: Record<string, string> = {
      'refnumber': 'RefNumber',
      'suppliername': 'SupplierName',
      'expecteddate': 'ExpectedDate',
      'actualarrivaldate': 'ActualArrivalDate',
      'expectedboxcount': 'ExpectedBoxCount',
      'receivedboxcount': 'ReceivedBoxCount',
      'status': 'Status',
    };
    
    return fieldMapping[field.toLowerCase()] || field;
  }, []);

  const fetchShipments = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
      grouping?: GroupQueryOptions;
    }) => {
      const isGrouped = params.grouping && params.grouping.GroupBy.length > 0;
      
      const queryParams = new URLSearchParams({
        PageNumber: String(params.page + 1),
        PageSize: String(params.pageSize),
      });

      if (params.search) queryParams.append('SearchTerm', params.search);
      
      let res: Response;

      if (isGrouped && params.grouping) {
        // POST request for grouped data with query parameters and empty payload
        params.grouping.GroupBy.forEach(field => {
          queryParams.append('GroupBy', mapFieldForGrouping(field));
        });
        queryParams.append('Level', String(params.grouping.Level));
        queryParams.append('GroupSortBy', params.grouping.GroupSortBy || 'key');
        queryParams.append('GroupSortDescending', String(params.grouping.GroupSortDescending || false));
        queryParams.append('IncludeCount', String(params.grouping.IncludeCount || true));
        
        if (params.grouping.ParentKey) {
          queryParams.append('ParentKey', params.grouping.ParentKey);
        }

        res = await DataService.get(`api/shipment/grouped?${queryParams.toString()}`, {});
      } else {
        // GET request for normal data
        if (params.sortModel?.length > 0) {
          const { field, sort } = params.sortModel[0];
          queryParams.append('SortBy', field);
          queryParams.append('SortDescending', String(sort === 'desc'));
        }

        res = await DataService.get(`api/shipment?${queryParams.toString()}`);
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to fetch shipments`);
      const json = await res.json();

      let items: Shipment[] = [];
      let groups: any[] = [];
      
      if (isGrouped) {
        if (json?.data?.groups) {
          groups = Array.isArray(json.data.groups) ? json.data.groups : Object.values(json.data.groups);
        } else if (Array.isArray(json?.groups)) {
          groups = json.groups;
        }
      } else {
        if (json?.data?.items) {
          if (Array.isArray(json.data.items)) items = json.data.items;
          else if (typeof json.data.items === 'object') items = Object.values(json.data.items);
        } else if (Array.isArray(json?.items)) {
          items = json.items;
        }
      }

      return {
        items: isGrouped ? undefined : items,
        groups: isGrouped ? groups : undefined,
        totalCount: json?.data?.totalCount ?? json?.totalCount ?? (isGrouped ? groups.length : items.length),
        totalGroups: isGrouped ? (json?.data?.totalGroups ?? json?.totalGroups ?? json?.data?.totalCount ?? json?.totalCount ?? groups.length) : undefined,
        pageNumber: json?.data?.pageNumber ?? json?.pageNumber ?? params.page + 1,
        pageSize: json?.data?.pageSize ?? json?.pageSize ?? params.pageSize,
        totalPages:
          json?.data?.totalPages ??
          json?.totalPages ??
          Math.ceil(
            (isGrouped ? (json?.data?.totalGroups ?? json?.totalGroups ?? json?.data?.totalCount ?? json?.totalCount ?? groups.length) : (json?.data?.totalCount ?? json?.totalCount ?? items.length)) / params.pageSize
          ),
        hasNext: json?.data?.hasNext ?? json?.hasNext ?? false,
        hasPrevious: json?.data?.hasPrevious ?? json?.hasPrevious ?? false,
      };
    },
    [mapFieldForGrouping]
  );

  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'refNumber',
      headerName: 'Ref Number',
      flex: 1,
      minWidth: 150,
      groupable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 'medium' }}
          onClick={() => navigate(`/shipments/${params.row.refNumber}`)}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'supplierName',
      headerName: 'Supplier',
      flex: 1.5,
      minWidth: 160,
      groupable: true,
    },
    {
      field: 'expectedDate',
      headerName: 'Expected Date',
      flex: 1,
      minWidth: 130,
      type: 'date',
      groupable: true,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'actualArrivalDate',
      headerName: 'Actual Arrival Date',
      flex: 1,
      minWidth: 150,
      type: 'date',
      groupable: true,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'expectedBoxCount',
      headerName: 'Expected Boxes',
      width: 130,
      type: 'number',
      groupable: true,
    },
    {
      field: 'receivedBoxCount',
      headerName: 'Actual Boxes',
      width: 120,
      type: 'number',
      groupable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      groupable: true,
      renderCell: renderStatus,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      groupable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleEditShipment(params.row);
            }}
            title="Edit Shipment"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {params.row.report && (
            <IconButton
              size="small"
              color="secondary"
              onClick={(e) => {
                e.stopPropagation();
                handleFileDownload(params.row.report, `shipment-${params.row.refNumber}-report.xlsx`, params.row);
              }}
              sx={{
                opacity: params.row.hasDiscrepancy ? 0.5 : 1
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          )}
          {params.row.implementationFile && (
            <IconButton
              size="small"
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                handleFileDownload(params.row.implementationFile, `shipment-${params.row.refNumber}-implementation.xlsx`, params.row);
              }}
              sx={{
                opacity: params.row.hasDiscrepancy ? 0.5 : 1
              }}
            >
              <AssignmentIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const actions: PaginatedDataGridAction[] = [
    {
      key: 'addShipment',
      title: 'Add Shipment',
      icon: <AddIcon />,
      onClick: handleAddShipment,
      disabled: () => false,
      color: 'primary',
    },
  ];

  const handleDrawerSuccess = useCallback(() => {
    setDrawerOpen(false);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({ message: 'Shipment created successfully!', type: 'success' }));
  }, [dispatch]);

  const handleUpdateSuccess = useCallback(() => {
    setUpdateDrawerOpen(false);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({ message: 'Shipment updated successfully!', type: 'success' }));
  }, [dispatch]);

  const handleRowClick = (params: any) => navigate(`/shipments/${params.row.refNumber}`);

  return (
    <Box p={3}>
      <PaginatedDataGrid
        key={refreshKey}
        title="Shipments"
        columns={columns}
        idField="id"
        useBackendPagination
        onDataFetch={fetchShipments}
        cacheTimeout={300000}
        actions={actions}
        enableGrouping={true}
        enableGroupDrillDown={true}
        fieldMapper={mapFieldForGrouping}
        wmsMode
        groupedUrl='api/shipment/grouped/'
        densityLevel="compact"
        striped
        autoHeight={true}
        compactEmptyState={true}
        responsiveColumns={true}
        hideFooterWhenEmpty={true}
        maxHeight={700}
        onRowClick={handleRowClick}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'background.default',
          },
        }}
      />
      
      <ShipmentDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        onSuccess={handleDrawerSuccess}
      />
      
      <UpdateShipmentDrawer
        open={updateDrawerOpen}
        onClose={handleUpdateDrawerClose}
        onSuccess={handleUpdateSuccess}
        shipment={selectedShipment}
      />
    </Box>
  );
};

export default ShipmentListing;